/** Notificaciones push: que le llegue un aviso al celular aunque tenga
 *  la app cerrada.
 *
 *  El navegador exige la llave pública en bytes crudos, no en el texto
 *  base64url que entrega `web-push generate-vapid-keys` —de ahí la
 *  conversión de abajo. La suscripción que resulta (un "endpoint" único
 *  por dispositivo, más dos llaves para cifrar) se guarda en
 *  `push_subscripciones`; `functions/api/enviar-notificacion.ts` la
 *  usa para mandar el aviso de verdad. */

import { supabase, supabaseConfigurado } from '../data/supabaseAdapter'
import { uid } from './cn'

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

function base64UrlABytes(base64Url: string): Uint8Array {
  const relleno = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + relleno).replace(/-/g, '+').replace(/_/g, '/')
  const cruda = atob(base64)
  const bytes = new Uint8Array(cruda.length)
  for (let i = 0; i < cruda.length; i++) bytes[i] = cruda.charCodeAt(i)
  return bytes
}

/** iPhone o iPad. La razón de por qué esto importa está en
 *  `pushDisponible` de abajo: en iOS las notificaciones dependen de
 *  cómo se abrió la app, no solo del navegador. */
export function esIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

/** True si esto se abrió como app instalada (ícono en la pantalla de
 *  inicio), no como una pestaña más de Safari o Chrome. En iOS es la
 *  diferencia entre "las notificaciones pueden funcionar" y "no existe
 *  ninguna forma de que funcionen, ni pidiendo permiso ni nada" —Apple
 *  no le da la API de Push a una pestaña común, recién se la da a la
 *  app instalada. Se revisan dos formas de detectarlo porque Safari
 *  viejo en iOS no entiende `display-mode` y solo entiende
 *  `navigator.standalone`. */
export function appInstalada(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/** True si el navegador puede recibir push y hay llave pública
 *  configurada. Safari de escritorio y algunos navegadores en modo
 *  privado no lo soportan —mejor no ofrecer el botón que ofrecerlo y
 *  que falle sin explicación.
 *
 *  En iPhone hay una condición más: si no está instalada a la pantalla
 *  de inicio, "PushManager" directamente no existe en el navegador —no
 *  es que el permiso se vaya a negar, es que la función ni aparece. Acá
 *  se corta antes de intentar, para no ofrecer un botón que no puede
 *  funcionar de ninguna manera. */
export function pushDisponible(): boolean {
  if (esIOS() && !appInstalada()) return false
  return (
    Boolean(VAPID_PUBLIC) &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** En qué quedó el permiso: nunca preguntado, aceptado, o rechazado.
 *  Rechazado no tiene vuelta atrás desde la app —la persona tiene que
 *  habilitarlo desde los ajustes del navegador—, así que conviene
 *  avisarlo así en vez de fallar en silencio. */
export function permisoNotificaciones(): NotificationPermission | 'no-soportado' {
  if (!pushDisponible()) return 'no-soportado'
  return Notification.permission
}

/** Pide el permiso (si hace falta) y guarda la suscripción de este
 *  dispositivo para ese usuario. Se puede llamar de nuevo sin problema
 *  —si ya estaba suscrito, `subscribe` de PushManager devuelve la misma
 *  suscripción en vez de crear una repetida. */
export async function suscribirse(usuarioId: string): Promise<'ok' | 'rechazado' | 'no-soportado'> {
  if (!pushDisponible() || !supabaseConfigurado || !supabase) return 'no-soportado'

  const permiso = await Notification.requestPermission()
  if (permiso !== 'granted') return 'rechazado'

  const registro = await navigator.serviceWorker.ready
  const suscripcion = await registro.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlABytes(VAPID_PUBLIC!) as BufferSource,
  })

  const json = suscripcion.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return 'no-soportado'

  const { error } = await supabase.from('push_subscripciones').upsert(
    {
      id: uid('push'),
      usuarioId,
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
    { onConflict: 'endpoint' },
  )
  if (error) throw error
  return 'ok'
}

/** Manda un push de verdad —vía `functions/api/enviar-notificacion.ts`—
 *  a todos los celulares suscritos de esa persona. No lanza si falla:
 *  quien llama a esto (por ejemplo, registrar una venta) no debe caerse
 *  porque el despachador no tenga las notificaciones activadas. */
export async function enviarNotificacion(
  usuarioId: string,
  titulo: string,
  cuerpo: string,
  url?: string,
): Promise<void> {
  try {
    await fetch('/api/enviar-notificacion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ usuarioId, titulo, cuerpo, url }),
    })
  } catch {
    // Sin señal, o la función de Cloudflare no respondió: no es motivo
    // para que la venta o el cambio de estado fallen.
  }
}

/** Saca la suscripción de este dispositivo —de la nube y del navegador. */
export async function desuscribirse(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const registro = await navigator.serviceWorker.getRegistration()
  const suscripcion = await registro?.pushManager.getSubscription()
  if (!suscripcion) return
  const endpoint = suscripcion.endpoint
  await suscripcion.unsubscribe()
  if (supabase) await supabase.from('push_subscripciones').delete().eq('endpoint', endpoint)
}
